import { useState, useEffect } from 'react';
import { FiPlus, FiEye, FiUsers, FiGrid, FiStar } from 'react-icons/fi';
import api from '../../utils/api';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import { formatDate } from '../../utils/dateHelpers';

const COURSE_TYPES = ['BLS', 'ACLS', 'ATLS', 'NRP'];
const STATUS_MAP = { 'Planned': 'planned', 'In Progress': 'in-progress', 'Completed': 'completed' };

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [attendeesModal, setAttendeesModal] = useState(null);
  const [satisfactionModal, setSatisfactionModal] = useState(null);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const data = await api.get('/courses');
      setCourses(data.courses || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCourses(); }, []);

  const handleSave = async (formData) => {
    try {
      if (editItem?.id) {
        await api.put(`/courses/${editItem.id}`, formData);
      } else {
        await api.post('/courses', formData);
      }
      setEditModal(false);
      setEditItem(null);
      fetchCourses();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (row) => {
    if (!confirm(`Delete course "${row.course_name}" on ${formatDate(row.start_date)}?`)) return;
    try {
      await api.delete(`/courses/${row.id}`);
      fetchCourses();
    } catch (err) {
      alert(err.message);
    }
  };

  const generateQR = async (courseId, type) => {
    try {
      const data = await api.post(`/courses/${courseId}/generate-${type}-qr`);
      setQrModal({ type, data });
    } catch (err) {
      alert(err.message);
    }
  };

  const viewAttendees = async (courseId) => {
    try {
      const data = await api.get(`/courses/${courseId}/attendees`);
      setAttendeesModal(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const viewSatisfaction = async (courseId) => {
    try {
      const data = await api.get(`/courses/${courseId}/satisfaction`);
      setSatisfactionModal(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const downloadQR = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename + '.png';
    link.click();
  };

  const columns = [
    { key: 'course_name', label: 'Course', render: (v) => <Badge status={v === 'BLS' ? 'valid' : v === 'ACLS' ? 'expiring' : v === 'ATLS' ? 'planned' : 'active'} text={v} /> },
    { key: 'start_date', label: 'Start', render: (v) => formatDate(v) },
    { key: 'end_date', label: 'End', render: (v) => formatDate(v) },
    { key: 'location', label: 'Location' },
    { key: 'instructor_name', label: 'Instructor' },
    { key: 'attendee_count', label: 'Attendees' },
    { key: 'status', label: 'Status', render: (v) => <Badge status={STATUS_MAP[v]} text={v} /> },
    {
      key: 'actions_qr', label: 'QR / Details',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => generateQR(row.id, 'attendance')} title="Attendance QR">
            <FiGrid size={12} /> Attend
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => generateQR(row.id, 'satisfaction')} title="Survey QR">
            <FiStar size={12} /> Survey
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => viewAttendees(row.id)} title="View Attendees">
            <FiUsers size={12} />
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => viewSatisfaction(row.id)} title="View Satisfaction">
            <FiEye size={12} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Life Support Courses</h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditItem(null); setEditModal(true); }}>
            <FiPlus size={14} /> Add Course
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card text-center p-4"><div className="spinner" style={{ margin: '40px auto' }} /></div>
      ) : (
        <DataTable columns={columns} data={courses} onEdit={(row) => { setEditItem(row); setEditModal(true); }} onDelete={handleDelete} />
      )}

      <Modal isOpen={editModal} onClose={() => { setEditModal(false); setEditItem(null); }} title={editItem ? 'Edit Course' : 'Add Course'}>
        <CourseForm course={editItem} onSave={handleSave} onCancel={() => { setEditModal(false); setEditItem(null); }} />
      </Modal>

      <Modal isOpen={!!qrModal} onClose={() => setQrModal(null)} title={`${qrModal?.type === 'attendance' ? 'Attendance' : 'Satisfaction'} QR Code`}>
        {qrModal && (
          <div style={{ textAlign: 'center' }}>
            <img src={qrModal.data.qr_data_url} alt="QR Code" style={{ maxWidth: '300px', margin: '0 auto 16px' }} />
            <p className="text-sm text-muted mb-3">Scan to submit {qrModal.type}</p>
            <p className="text-xs text-muted mb-3" style={{ wordBreak: 'break-all' }}>{qrModal.data.qr_url}</p>
            <button className="btn btn-primary btn-sm" onClick={() => downloadQR(qrModal.data.qr_data_url, `${qrModal.type}_qr`)}>Download PNG</button>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!attendeesModal} onClose={() => setAttendeesModal(null)} title={`Attendees (${attendeesModal?.count || 0})`}>
        {attendeesModal && (
          attendeesModal.attendees.length === 0 ? (
            <p className="text-muted text-center p-3">No attendees recorded yet.</p>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Employee ID</th><th style={thStyle}>Name</th><th style={thStyle}>Department</th><th style={thStyle}>Time</th></tr></thead>
                <tbody>
                  {attendeesModal.attendees.map((a) => (
                    <tr key={a.id}><td style={tdStyle}>{a.employee_id}</td><td style={tdStyle}>{a.staff_name}</td><td style={tdStyle}>{a.department}</td><td style={tdStyle}>{formatDate(a.scanned_at)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Modal>

      <Modal isOpen={!!satisfactionModal} onClose={() => setSatisfactionModal(null)} title={`Satisfaction (${satisfactionModal?.count || 0} responses)`}>
        {satisfactionModal && satisfactionModal.count > 0 ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[['Overall Quality', satisfactionModal.averages.q1], ['Relevance', satisfactionModal.averages.q2], ['Instructor', satisfactionModal.averages.q3], ['Organization', satisfactionModal.averages.q4]].map(([l, v]) => (
                <div key={l} style={{ padding: '12px', backgroundColor: '#F5F0E8', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2B362E' }}>{v}/5</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e8f0e5', borderRadius: '8px' }}>
              <span className="text-sm font-medium">{satisfactionModal.averages.recommend_pct}% would recommend</span>
            </div>
          </div>
        ) : satisfactionModal ? (
          <p className="text-muted text-center p-3">No survey responses yet.</p>
        ) : null}
      </Modal>
    </div>
  );
}

function CourseForm({ course, onSave, onCancel }) {
  const [form, setForm] = useState({
    course_name: course?.course_name || 'BLS',
    start_date: course?.start_date || '',
    end_date: course?.end_date || '',
    location: course?.location || '',
    instructor_name: course?.instructor_name || '',
    status: course?.status || 'Planned',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div className="form-row">
        <div className="form-group">
          <label>Course Type *</label>
          <select value={form.course_name} onChange={(e) => set('course_name', e.target.value)}>
            {COURSE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={form.status} onChange={(e) => set('status', e.target.value)}>
            <option value="Planned">Planned</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group"><label>Start Date *</label><input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} required /></div>
        <div className="form-group"><label>End Date *</label><input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} required /></div>
      </div>
      <div className="form-group"><label>Location</label><input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Venue or room" /></div>
      <div className="form-group"><label>Instructor Name</label><input value={form.instructor_name} onChange={(e) => set('instructor_name', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}

const thStyle = { padding: '10px 12px', fontSize: '0.8rem', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #f0ece4', color: '#2B362E' };
const tdStyle = { padding: '10px 12px', fontSize: '0.85rem', borderBottom: '1px solid #f0ece4' };
